import MarketingLayout from "@/components/MarketingLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { Check, CreditCard, ExternalLink, Lock, Zap } from "lucide-react";

export default function Pricing() {
  return (
    <MarketingLayout>
      <section className="container py-16">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Value prop */}
          <div className="rounded-lg border border-border bg-muted/40 px-5 py-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-foreground">Secure secrets management for vibe-coding and AI agents.</p>
              <p className="text-sm text-muted-foreground mt-0.5">Stop hardcoding API keys — use short-lived tokens with IP allowlists instead.</p>
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6" /> Pricing
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your plan and usage.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Install the client from{" "}
              <a
                href="https://pypi.org/project/double-o/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                PyPI
                <ExternalLink className="w-3 h-3" />
              </a>
              .
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Plans</h2>
            <div className="grid gap-4 md:grid-cols-3 items-start">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Free</CardTitle>
                  <CardDescription>
                    <span className="text-xl font-bold text-foreground">€0</span>/mo
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 secret</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1,000 API calls/mo</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 req/sec</li>
                  </ul>
                  <Button className="w-full" size="sm" variant="outline" asChild>
                    <a href={getLoginUrl()}>Get Started Free</a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-primary shadow-md ring-1 ring-primary relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-3 py-0.5 text-xs font-semibold">Most Popular</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-1">
                    <Zap className="w-4 h-4 text-yellow-500" /> Pro
                  </CardTitle>
                  <CardDescription>
                    <span className="text-xl font-bold text-foreground">€5</span>/mo
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 secrets</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 100,000 API calls/mo</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 req/sec</li>
                  </ul>
                  <Button className="w-full" size="sm" asChild>
                    <a href={getLoginUrl()}>Upgrade to Pro</a>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-1">
                      <Zap className="w-4 h-4 text-purple-500" /> Max
                    </CardTitle>
                  </div>
                  <CardDescription>
                    <span className="text-xl font-bold text-foreground">€20</span>/mo
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited secrets</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited API calls</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 100 req/sec</li>
                  </ul>
                  <Button className="w-full" size="sm" asChild>
                    <a href={getLoginUrl()}>Upgrade to Max</a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
